import { create } from "zustand";

/**
 * Auth store — minimal pre-API state pro Phase 8 shell.
 *
 * Production wire-up (Phase 8 follow-ups): /api/auth/me probe v `__root.tsx`
 * loadne userInfo do store; logout volá /api/public/auth/logout a vyčistí
 * cookie + state.
 *
 * Phase 8 scope:
 *   - role je odvozeno z backend JWT claims (`role`). Frontend ho **NEČTE**
 *     z JWT (HttpOnly cookie); místo toho voláme /api/auth/me, který vrátí
 *     `{ userId, role, tenantId? }` a do store dosadíme.
 *   - Bez serveru store zůstane v `state="anonymous"` a guard redirectne na /login.
 *
 * Role enum z `spec/normative/GLOSSARY.md` (per DEC-011 anglické pojmenování):
 *   - 'globalAdmin' → admin shell (Petr)
 *   - 'tenantAdmin' → tenant shell s admin oprávněními (per-Středisko)
 *   - 'subAdmin' → tenant shell (Milan, microgroup owner)
 *   - 'endUser' → tenant shell (Zdeněk, member)
 */

export type Role = "globalAdmin" | "tenantAdmin" | "subAdmin" | "endUser";

export interface AuthUser {
  userId: string;
  role: Role;
  /** Tenant scope; null pro globalAdmin. */
  tenantId: string | null;
  email: string;
  /** Per DEC-013 — pokud TRUE, frontend musí redirect na /consent/re-prompt. */
  requiresConsentRePrompt?: boolean;
}

export type AuthState =
  | { state: "loading" }
  | { state: "anonymous" }
  | { state: "authenticated"; user: AuthUser };

interface AuthStoreShape {
  auth: AuthState;
  setLoading: () => void;
  setAnonymous: () => void;
  setAuthenticated: (user: AuthUser) => void;
  /** Logout — clear state. */
  reset: () => void;
}

export const useAuthStore = create<AuthStoreShape>((set) => ({
  auth: { state: "loading" },
  setLoading: () => set({ auth: { state: "loading" } }),
  setAnonymous: () => set({ auth: { state: "anonymous" } }),
  setAuthenticated: (user) => set({ auth: { state: "authenticated", user } }),
  reset: () => set({ auth: { state: "anonymous" } }),
}));

/**
 * Read snapshot mimo React komponent (např. v `beforeLoad` route guard).
 */
export function readAuthSnapshot(): AuthState {
  return useAuthStore.getState().auth;
}

/**
 * Helper pro testy — direct injection bez async fetch /api/auth/me.
 */
export function _testSetAuth(state: AuthState): void {
  if (state.state === "authenticated") {
    useAuthStore.setState({ auth: state });
  } else {
    useAuthStore.setState({ auth: state });
  }
}

/** Convenience boolean shortcuts. */
export function isAdmin(state: AuthState): boolean {
  return state.state === "authenticated" && state.user.role === "globalAdmin";
}

export function isTenant(state: AuthState): boolean {
  if (state.state !== "authenticated") return false;
  return (
    state.user.role === "tenantAdmin" ||
    state.user.role === "subAdmin" ||
    state.user.role === "endUser"
  );
}

/** Hydrate auth ze /api/auth/me — volá se v `__root.tsx` ve `useEffect`. */
export async function hydrateAuth(): Promise<AuthState> {
  try {
    const r = await fetch("/api/auth/me", {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (r.status === 401 || r.status === 403) {
      useAuthStore.getState().setAnonymous();
      return { state: "anonymous" };
    }
    if (!r.ok) {
      useAuthStore.getState().setAnonymous();
      return { state: "anonymous" };
    }
    const data = (await r.json()) as Partial<AuthUser>;
    if (!data.userId || !data.role) {
      useAuthStore.getState().setAnonymous();
      return { state: "anonymous" };
    }
    const user: AuthUser = {
      userId: data.userId,
      role: data.role,
      tenantId: data.tenantId ?? null,
      email: data.email ?? "",
      requiresConsentRePrompt: data.requiresConsentRePrompt ?? false,
    };
    useAuthStore.getState().setAuthenticated(user);
    return { state: "authenticated", user };
  } catch {
    useAuthStore.getState().setAnonymous();
    return { state: "anonymous" };
  }
}
